/**
 * My Day agenda merge (newui.md §4.6 / §17.1).
 * Merges today's calendar events and due/overdue tasks into one chronological
 * rail. Overdue floats to the top; timed items sort by time; everything is
 * zero-safe so a missing date never throws or produces "Invalid Date".
 */
export interface RawEvent {
  id?: string | number;
  title?: string | null;
  startDate?: string | null;
}

export interface RawTask {
  id?: string | number;
  title?: string | null;
  deadline?: string | null;
  completedAt?: string | null;
  completed_at?: string | null;
}

export type MyDaySource = 'Calendar' | 'Workspace';

export interface MyDayItem {
  key: string;
  timeLabel: string; // "09:00", "All day", or "Overdue"
  title: string;
  source: MyDaySource;
  overdue: boolean;
  sortKey: number; // ms since epoch used for ordering (overdue forced negative)
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function hhmm(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function isTaskOpen(t: RawTask): boolean {
  return !t.completedAt && !t.completed_at;
}

export function buildMyDay(
  events: RawEvent[] | null | undefined,
  tasks: RawTask[] | null | undefined,
  now: Date = new Date(),
  limit = 8,
): MyDayItem[] {
  const items: MyDayItem[] = [];

  (events || []).forEach((e, i) => {
    const start = toDate(e.startDate);
    if (!start || !sameDay(start, now)) return;
    const midnight = start.getHours() === 0 && start.getMinutes() === 0;
    items.push({
      key: `e-${e.id ?? i}`,
      timeLabel: midnight ? 'All day' : hhmm(start),
      title: (e.title || 'Untitled event').toString(),
      source: 'Calendar',
      overdue: false,
      sortKey: midnight ? start.getTime() - 1 : start.getTime(),
    });
  });

  (tasks || []).forEach((t, i) => {
    if (!isTaskOpen(t)) return;
    const due = toDate(t.deadline);
    if (!due) return;
    const overdue = due.getTime() < now.getTime();
    const dueToday = sameDay(due, now);
    if (!overdue && !dueToday) return; // only today's or already-late work
    items.push({
      key: `t-${t.id ?? i}`,
      timeLabel: overdue ? 'Overdue' : hhmm(due),
      title: (t.title || 'Untitled task').toString(),
      source: 'Workspace',
      overdue,
      // Overdue tasks are forced ahead of everything with a negative key,
      // most-overdue first.
      sortKey: overdue ? -(now.getTime() - due.getTime()) - 1e13 : due.getTime(),
    });
  });

  items.sort((a, b) => a.sortKey - b.sortKey);
  return items.slice(0, Math.max(0, limit));
}
